import { useQuery } from '@tanstack/react-query';
import { academyService } from '@/services/academyService';
import type {
  AcademyLandingResult,
  AcademyModuleDetailResult,
  AcademyResult,
} from '@/types/academy/academy';

export const ACADEMY_QUERY_KEY = ['academy'] as const;

export const academyEnrollmentsQueryKey = [
  ...ACADEMY_QUERY_KEY,
  'enrollments',
] as const;

export const ACADEMY_QUERY_DEFAULTS = {
  refetchOnWindowFocus: false,
  ...(import.meta.env.VITE_APP_ENV === 'production'
    ? { staleTime: 60 * 1000, gcTime: 5 * 60 * 1000 }
    : {}),
};

/**
 * Signed-in landing data is enrollment-derived, so it sits under the enrollments
 * key: login, logout and checkout already invalidate that prefix.
 */
export const academyLandingQueryKey = [
  ...academyEnrollmentsQueryKey,
  'landing',
] as const;

/** Prefix shared by a module's public + private detail queries — invalidate this to refresh both. */
export const academyModuleQueryKeyPrefix = (moduleId: string) =>
  [...ACADEMY_QUERY_KEY, 'module', moduleId] as const;

export const academyModuleQueryKey = (
  moduleId: string,
  authenticated = false,
) =>
  [
    ...academyModuleQueryKeyPrefix(moduleId),
    authenticated ? 'private' : 'public',
  ] as const;

/**
 * Fetches all public academy data (tiers, modules, stats).
 *
 * Pass `enabled: false` to hold the request back when it isn't needed yet
 * (e.g. My Learning only needs it for the module-numbering lookup).
 */
export const useAcademy = (enabled = true) =>
  useQuery<AcademyResult | null>({
    queryKey: ACADEMY_QUERY_KEY,
    queryFn: async () => {
      const response = await academyService.getAcademy();
      return response.result ?? null;
    },
    enabled,
    ...ACADEMY_QUERY_DEFAULTS,
  });

/**
 * Fetches landing page academy data from the public or private API.
 * Signed-in users get the private response, which also carries their first few
 * enrolled modules — so the landing page needs no separate enrollments request.
 */
export const useAcademyLanding = (isAuthenticated: boolean) =>
  useQuery<AcademyResult | AcademyLandingResult | null>({
    queryKey: isAuthenticated ? academyLandingQueryKey : ACADEMY_QUERY_KEY,
    queryFn: async () => {
      const response = isAuthenticated
        ? await academyService.getAcademyWithEnrollments()
        : await academyService.getAcademy();
      return response.result ?? null;
    },
    ...ACADEMY_QUERY_DEFAULTS,
  });

/**
 * Fetches module detail from the public or private API.
 * Pass `authenticated: true` for logged-in users — private response includes isEnrolled and expiresAt.
 */
export const useAcademyModule = (
  moduleId: string,
  options?: { authenticated?: boolean },
) => {
  const usePrivateEndpoint = options?.authenticated ?? false;

  return useQuery<AcademyModuleDetailResult | null>({
    queryKey: academyModuleQueryKey(moduleId, usePrivateEndpoint),
    queryFn: async () => {
      const response = usePrivateEndpoint
        ? await academyService.getEnrolledModuleById(moduleId)
        : await academyService.getAcademyModuleById(moduleId);
      return response.result ?? null;
    },
    enabled: Boolean(moduleId),
    ...ACADEMY_QUERY_DEFAULTS,
    retry: false,
  });
};
